const acNavigationFocusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'summary',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const acNavigationFocusableElements = (root) => {
  if (!(root instanceof Element)) return [];
  return [...root.querySelectorAll(acNavigationFocusableSelector)].filter((element) => (
    !element.hidden
    && !element.closest('[hidden], [inert]')
    && (!element.closest('details:not([open])') || element.matches('details:not([open]) > summary'))
    && element.getClientRects().length > 0
  ));
};

const acNavigationPrefersReducedMotion = (navigation) => (
  navigation?.dataset.motion === 'reduced'
  || document.body?.dataset.acMotion === 'reduced'
  || window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const acNavigationMotionValue = (element, property) => {
  if (!(element instanceof Element) || acNavigationPrefersReducedMotion(element)) return 0;
  return acTransitionTime(getComputedStyle(element).getPropertyValue(property));
};

const acTransitionTime = (value) => {
  const normalized = value.trim();
  if (normalized.endsWith('ms')) return Number.parseFloat(normalized) || 0;
  if (normalized.endsWith('s')) return (Number.parseFloat(normalized) || 0) * 1000;
  return 0;
};

const acComputedTransitionDuration = (element, property, pseudoElement = '') => {
  if (!(element instanceof Element)) return 0;
  const style = getComputedStyle(element, pseudoElement || null);
  const properties = style.transitionProperty.split(',').map((value) => value.trim());
  const durations = style.transitionDuration.split(',').map(acTransitionTime);
  const delays = style.transitionDelay.split(',').map(acTransitionTime);
  return properties.reduce((maximum, current, index) => {
    if (current !== 'all' && current !== property) return maximum;
    const duration = durations[index % durations.length] || 0;
    const delay = delays[index % delays.length] || 0;
    return Math.max(maximum, duration + delay);
  }, 0);
};

/** @param {Element|null} element @param {{property?: string, pseudoElement?: string, fallback?: number, reduced?: boolean, signal?: AbortSignal}} options */
const acWaitForTransition = (element, options = {}) => new Promise((resolve) => {
  const {
    property = 'transform', pseudoElement = '', reduced = false, signal,
  } = options;
  const fallback = options.fallback ?? acComputedTransitionDuration(element, property, pseudoElement);
  if (!(element instanceof Element) || reduced || fallback <= 0 || signal?.aborted) {
    resolve();
    return;
  }
  let settled = false;
  let timer = 0;
  const finish = () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timer);
    element.removeEventListener('transitionend', handleTransitionEnd);
    element.removeEventListener('transitioncancel', handleTransitionEnd);
    signal?.removeEventListener('abort', finish);
    resolve();
  };
  const handleTransitionEnd = (event) => {
    if (event.target !== element || event.propertyName !== property) return;
    if (pseudoElement && event.pseudoElement !== pseudoElement) return;
    finish();
  };
  timer = window.setTimeout(finish, fallback + 80);
  element.addEventListener('transitionend', handleTransitionEnd);
  element.addEventListener('transitioncancel', handleTransitionEnd);
  signal?.addEventListener('abort', finish, { once: true });
});

class AcHeaderShellController {
  #footerObserver;
  #footerFrame = 0;
  #header;
  #host;

  constructor(host, header) {
    this.#host = host;
    this.#header = header;
  }

  connect(signal) {
    window.addEventListener('scroll', this.syncScrollState, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleFooterObservation, { passive: true, signal });
    document.addEventListener('shopify:section:load', this.#scheduleFooterObservation, { signal });
    document.addEventListener('shopify:section:unload', this.#scheduleFooterObservation, { signal });
    this.#observeFooter();
    this.syncScrollState();
  }

  disconnect() {
    cancelAnimationFrame(this.#footerFrame);
    this.#footerObserver?.disconnect();
  }

  syncScrollState = () => {
    if (this.#host.dataset.navigationOpen === 'true' || this.#host.classList.contains('is-footer-hidden')) return;
    const threshold = Number.parseInt(this.#host.dataset.compactThreshold || '16', 10);
    const compact = this.#host.dataset.sticky === 'true' && window.scrollY > threshold;
    const state = compact ? 'compact' : 'top';
    if (this.#host.dataset.shellState !== state) this.#host.dataset.shellState = state;
    if (this.#header.classList.contains('is-compact') !== compact) this.#header.classList.toggle('is-compact', compact);
  };

  setNavigationOpen(open) {
    this.#host.dataset.navigationOpen = String(open);
    if (open) this.#host.dataset.shellState = 'navigation-open';
    else this.syncScrollState();
  }

  #scheduleFooterObservation = () => {
    cancelAnimationFrame(this.#footerFrame);
    this.#footerFrame = requestAnimationFrame(() => {
      this.#footerFrame = 0;
      this.#observeFooter();
    });
  };

  #observeFooter = () => {
    this.#footerObserver?.disconnect();
    const footer = document.querySelector('[data-ac-ref-footer]');
    if (!footer) {
      this.#host.classList.remove('is-footer-hidden');
      this.syncScrollState();
      return;
    }
    this.#footerObserver = new IntersectionObserver(([entry]) => {
      this.#host.classList.toggle('is-footer-hidden', entry.isIntersecting);
      if (entry.isIntersecting && this.#host.dataset.navigationOpen !== 'true') this.#host.dataset.shellState = 'footer-hidden';
      else this.syncScrollState();
    }, {
      // Match the reference's footer top 40% trigger. Vertical percentage
      // root margins resolve against width, so use viewport-height pixels.
      rootMargin: `0px 0px -${window.innerHeight * 0.6}px 0px`,
      threshold: 0,
    });
    this.#footerObserver.observe(footer);
  };
}

class AcNavigationTreeController {
  #mobileQuery;
  #navigation;
  #onLeave;
  #onPreview;
  #onResize;
  #scrollPositions = new WeakMap();
  #submenuGenerations = new WeakMap();
  #submenuOpeners = new WeakMap();

  constructor({ navigation, mobileQuery, onPreview, onLeave, onResize }) {
    this.#navigation = navigation;
    this.#mobileQuery = mobileQuery;
    this.#onPreview = onPreview;
    this.#onLeave = onLeave;
    this.#onResize = onResize;
  }

  connect(signal) {
    this.#navigation.addEventListener('pointerover', this.#handlePointerOver, { signal });
    this.#navigation.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    this.#navigation.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.#navigation.addEventListener('scroll', this.#handleScroll, { capture: true, passive: true, signal });
    this.#mobileQuery.addEventListener('change', this.#handleBreakpointChange, { signal });
    this.syncScrollAffordances();
  }

  handleAction(target) {
    const expand = target.closest('[data-ac-navigation-expand]');
    if (expand && this.#navigation.contains(expand)) {
      const item = expand.closest('[data-ac-navigation-item]');
      if (item) {
        this.#submenuOpeners.set(item, expand);
        this.#onPreview(item);
        this.activateItem(item, true);
      }
      return true;
    }
    const back = target.closest('[data-ac-navigation-back]');
    if (back && this.#navigation.contains(back)) {
      const submenu = back.closest('.ac-ref-navigation__submenu');
      const item = submenu?.closest('[data-ac-navigation-item]');
      if (item) this.collapseItem(item, true);
      return true;
    }
    return false;
  }

  handleEscape() {
    const expandedItems = [...this.#navigation.querySelectorAll('.ac-ref-navigation__item.is-expanded')];
    const openSubmenu = expandedItems.at(-1);
    if (!openSubmenu) return false;
    this.collapseItem(openSubmenu, true);
    return true;
  }

  activateItem(item, moveFocus) {
    const submenu = item.querySelector(':scope > .ac-ref-navigation__submenu-shell > .ac-ref-navigation__submenu');
    const tree = item.closest('.ac-ref-navigation__tree');
    this.#onResize();
    for (const sibling of tree?.children || []) {
      if (sibling !== item && sibling.matches('[data-ac-navigation-item]')) this.collapseItem(sibling, false, !submenu);
    }
    if (!submenu) {
      requestAnimationFrame(this.syncScrollAffordances);
      return;
    }
    if (item.classList.contains('is-expanded')) return;
    if (this.#mobileQuery.matches) this.#scrollPositions.set(tree, tree.scrollTop);
    const generation = this.#nextSubmenuGeneration(submenu);
    // Establish the closed frame before starting container and row transitions together.
    submenu.dataset.state = 'closed';
    submenu.hidden = false;
    submenu.getBoundingClientRect();
    submenu.dataset.state = 'opening';
    item.classList.add('is-expanded');
    item.querySelectorAll(':scope > .ac-ref-navigation__item-row [data-ac-navigation-expand]').forEach(trigger => trigger.setAttribute('aria-expanded', 'true'));
    if (this.#mobileQuery.matches) {
      for (const sibling of tree?.children || []) {
        if (sibling.matches('[data-ac-navigation-item]')) sibling.inert = sibling !== item;
      }
      this.#setTreeMotionSuppressed(tree, true);
      const itemRow = item.querySelector(':scope > .ac-ref-navigation__item-row');
      if (itemRow) itemRow.inert = true;
    }
    void this.#settleSubmenu(item, submenu, generation, moveFocus);
    this.#syncExpandedDepth();
    requestAnimationFrame(this.syncScrollAffordances);
  }

  collapseItem(item, restoreFocus, immediate = false) {
    const submenu = item.querySelector(':scope > .ac-ref-navigation__submenu-shell > .ac-ref-navigation__submenu');
    if (!submenu) return;
    for (const expanded of submenu.querySelectorAll('.ac-ref-navigation__item.is-expanded')) this.collapseItem(expanded, false, immediate);
    const generation = this.#nextSubmenuGeneration(submenu);
    submenu.dataset.state = 'closing';
    item.classList.remove('is-expanded');
    const trigger = this.#submenuOpeners.get(item) || item.querySelector(':scope > .ac-ref-navigation__item-row [data-ac-navigation-expand]');
    item.querySelectorAll(':scope > .ac-ref-navigation__item-row [data-ac-navigation-expand]').forEach(control => control.setAttribute('aria-expanded', 'false'));
    const parentTree = item.closest('.ac-ref-navigation__tree');
    if (parentTree) {
      for (const sibling of parentTree.children) {
        if (sibling.matches('[data-ac-navigation-item]')) sibling.inert = false;
      }
      this.#setTreeMotionSuppressed(parentTree, false);
      const itemRow = item.querySelector(':scope > .ac-ref-navigation__item-row');
      if (itemRow) itemRow.inert = false;
      parentTree.scrollTop = this.#scrollPositions.get(parentTree) || 0;
    }
    void this.#hideSubmenu(item, submenu, generation, immediate);
    this.#syncExpandedDepth();
    if (restoreFocus) trigger?.focus({ preventScroll: true });
  }

  resetHierarchy(immediate = false) {
    const expandedRoots = [...this.#navigation.querySelectorAll('.ac-ref-navigation__item.is-expanded')]
      .filter((item) => !item.parentElement?.closest('.ac-ref-navigation__item.is-expanded'));
    for (const item of expandedRoots) this.collapseItem(item, false, immediate);
  }

  syncScrollAffordances = () => {
    if (this.#navigation.hidden) return;
    for (const region of this.#navigation.querySelectorAll('[data-ac-navigation-scroll]')) this.#syncScrollAffordance(region);
  };

  #handlePointerOver = (event) => {
    const item = event.target.closest('[data-ac-navigation-item]');
    if (!item || !this.#navigation.contains(item)) return;
    if (event.relatedTarget && item.contains(event.relatedTarget)) return;
    const nestedItem = Boolean(item.closest('.ac-ref-navigation__submenu'));
    if (!(this.#mobileQuery.matches && nestedItem)) this.#onPreview(item);
    if (this.#mobileQuery.matches) return;
    this.activateItem(item, false);
  };

  #handlePointerLeave = () => {
    if (this.#mobileQuery.matches) return;
    this.#onLeave();
  };

  #handleFocusIn = (event) => {
    const item = event.target.closest('[data-ac-navigation-item]');
    if (!item || !this.#navigation.contains(item)) return;
    const nestedItem = Boolean(item.closest('.ac-ref-navigation__submenu'));
    if (!(this.#mobileQuery.matches && nestedItem)) this.#onPreview(item);
    if (!this.#mobileQuery.matches && !event.target.closest('[data-ac-navigation-expand], [data-ac-navigation-back]')) {
      this.activateItem(item, false);
    }
  };

  #handleBreakpointChange = () => {
    this.resetHierarchy(true);
    requestAnimationFrame(this.syncScrollAffordances);
  };

  #handleScroll = (event) => {
    if (event.target instanceof HTMLElement && event.target.matches('[data-ac-navigation-scroll]')) this.#syncScrollAffordance(event.target);
  };

  #syncScrollAffordance(region) {
    const maxScroll = Math.max(0, region.scrollHeight - region.clientHeight);
    const scrollable = !region.hidden && region.getClientRects().length > 0 && maxScroll > 2;
    region.dataset.scrollable = String(scrollable);
    region.dataset.scrollStart = String(!scrollable || region.scrollTop <= 1);
    region.dataset.scrollEnd = String(!scrollable || region.scrollTop >= maxScroll - 1);
    region.tabIndex = scrollable ? 0 : -1;
    if (scrollable && !region.hasAttribute('aria-label')) {
      const navigation = region.closest('nav[aria-label]');
      if (navigation) region.setAttribute('aria-label', navigation.getAttribute('aria-label'));
    }
  }

  async #settleSubmenu(item, submenu, generation, moveFocus) {
    await acWaitForTransition(submenu, { reduced: acNavigationPrefersReducedMotion(this.#navigation) });
    if (this.#submenuGenerations.get(submenu) !== generation || !item.classList.contains('is-expanded') || submenu.hidden) return;
    submenu.dataset.state = 'open';
    if (moveFocus) acNavigationFocusableElements(submenu)[0]?.focus({ preventScroll: true });
    this.syncScrollAffordances();
  }

  async #hideSubmenu(item, submenu, generation, immediate) {
    if (!immediate) await acWaitForTransition(submenu, { reduced: acNavigationPrefersReducedMotion(this.#navigation) });
    if (this.#submenuGenerations.get(submenu) !== generation || item.classList.contains('is-expanded')) return;
    submenu.hidden = true;
    submenu.dataset.state = 'closed';
    this.syncScrollAffordances();
  }

  #nextSubmenuGeneration(submenu) {
    const generation = (this.#submenuGenerations.get(submenu) || 0) + 1;
    this.#submenuGenerations.set(submenu, generation);
    return generation;
  }

  #setTreeMotionSuppressed(tree, suppressed) {
    if (!(tree instanceof Element)) return;
    for (const item of tree.querySelectorAll(':scope > [data-ac-navigation-item]')) {
      const motion = item.querySelector(':scope > .ac-ref-navigation__item-row > .ac-ref-navigation__item-motion');
      if (!motion) continue;
      if (suppressed) motion.dataset.suppressed = 'true';
      else delete motion.dataset.suppressed;
    }
  }

  #syncExpandedDepth() {
    const depths = [...this.#navigation.querySelectorAll('.ac-ref-navigation__item.is-expanded')]
      .map((item) => Number.parseInt(item.closest('[data-ac-navigation-level]')?.dataset.acNavigationLevel || '0', 10));
    this.#navigation.dataset.expandedDepth = String(Math.max(0, ...depths));
  }

}

class AcNavigationPreviewController {
  #activeCollageMarkup = '';
  #activeMediaKey = '';
  #collage;
  #collageAbortController;
  #collageCache = new Map();
  #collageContextRequest = 0;
  #collageCurrent;
  #collageGeneration = 0;
  #collageIncoming;
  #collageTimer = 0;
  #collageTransitionController;
  #defaultCollageMarkup = '';
  #getMode;
  #mediaGeneration = 0;
  #imageLoads = new Map();
  #mediaAbortController;
  #pendingMediaKey = '';
  #mediaTransitioning = false;
  #navigation;
  #onVisualState;
  #queuedMedia = null;
  #stageCurrent;
  #stageIncoming;
  #visualSwitches = 0;

  constructor({ navigation, getMode, onVisualState }) {
    this.#navigation = navigation;
    this.#getMode = getMode;
    this.#onVisualState = onVisualState;
    this.#collage = navigation.querySelector('[data-ac-navigation-collage]');
    this.#stageCurrent = navigation.querySelector('.ac-ref-navigation__stage-image--current');
    this.#stageIncoming = navigation.querySelector('.ac-ref-navigation__stage-image--incoming');
    this.#collageCurrent = this.#collage?.querySelector('.ac-ref-navigation__collage-group--current');
    this.#collageIncoming = this.#collage?.querySelector('.ac-ref-navigation__collage-group--incoming');
    this.#defaultCollageMarkup = this.#collageCurrent?.innerHTML || '';
    this.#activeCollageMarkup = this.#defaultCollageMarkup;
    this.#activeMediaKey = this.#descriptorKey(this.#descriptorFromImage(this.#stageCurrent));
  }

  disconnect() {
    this.cancel();
    window.clearTimeout(this.#collageTimer);
  }

  previewItem(item) {
    if (this.#navigation.dataset.contextMedia === 'true') {
      const template = item.querySelector(':scope > [data-ac-navigation-preview-template]');
      this.requestMedia(this.#descriptorFromTemplate(template) || this.modeMedia(this.#getMode()));
    }
    if (this.#navigation.dataset.contextCollage === 'true') this.#scheduleCollage(item.dataset.acNavigationContext);
  }

  restoreMode() {
    this.requestMedia(this.modeMedia(this.#getMode()));
    this.#restoreDefaultCollage();
  }

  modeMedia(mode) {
    return this.#descriptorFromTemplate(this.#navigation.querySelector(`[data-ac-navigation-mode-media="${CSS.escape(mode || 'menu')}"]`));
  }

  setStableMode(mode) {
    this.#setStableMedia(this.modeMedia(mode));
    this.#setStableCollage(this.#defaultCollageMarkup);
  }

  requestMedia(descriptor) {
    if (!descriptor || !this.#getMode()) return;
    this.#queuedMedia = descriptor;
    if (this.#pendingMediaKey !== this.#descriptorKey(descriptor)) this.#mediaAbortController?.abort();
    if (!this.#mediaTransitioning) void this.#drainMediaQueue();
  }

  cancel() {
    window.clearTimeout(this.#collageTimer);
    this.#mediaGeneration += 1;
    this.#collageGeneration += 1;
    this.#collageContextRequest += 1;
    this.#queuedMedia = null;
    this.#mediaAbortController?.abort();
    this.#collageAbortController?.abort();
    this.#collageTransitionController?.abort();
    this.#collageTransitionController = null;
    this.#resetInterruptedCollage();
    this.#visualSwitches = 0;
  }

  async #drainMediaQueue() {
    this.#mediaTransitioning = true;
    const generation = this.#mediaGeneration;
    try {
      while (this.#queuedMedia && this.#getMode() && generation === this.#mediaGeneration) {
        const descriptor = this.#queuedMedia;
        this.#queuedMedia = null;
        if (this.#descriptorKey(descriptor) === this.#activeMediaKey) continue;
        this.#pendingMediaKey = this.#descriptorKey(descriptor);
        this.#mediaAbortController = new AbortController();
        const loaded = await this.#preloadImage(descriptor, this.#mediaAbortController.signal);
        this.#mediaAbortController = null;
        this.#pendingMediaKey = '';
        if (!loaded || generation !== this.#mediaGeneration || !this.#getMode()) continue;
        if (this.#queuedMedia && this.#descriptorKey(this.#queuedMedia) !== this.#descriptorKey(descriptor)) continue;
        await this.#transitionMedia(descriptor, generation);
      }
    } finally {
      this.#mediaTransitioning = false;
      if (this.#queuedMedia && this.#getMode()) void this.#drainMediaQueue();
    }
  }

  async #transitionMedia(descriptor, generation) {
    if (!this.#stageCurrent || !this.#stageIncoming) return;
    if (acNavigationPrefersReducedMotion(this.#navigation)) {
      this.#setStableMedia(descriptor);
      return;
    }
    this.#applyDescriptor(this.#stageIncoming, descriptor);
    this.#stageIncoming.hidden = false;
    this.#stageIncoming.getBoundingClientRect();
    this.#beginVisualSwitch();
    try {
      this.#stageCurrent.classList.add('is-exiting');
      this.#stageIncoming.classList.add('is-entering');
      await acWaitForTransition(this.#stageIncoming, {
        fallback: acNavigationMotionValue(this.#navigation, '--ac-navigation-media-duration'),
      });
      if (generation !== this.#mediaGeneration || !this.#getMode()) {
        this.#resetMediaLayers();
        return;
      }
      const previous = this.#stageCurrent;
      const next = this.#stageIncoming;
      previous.hidden = true;
      previous.classList.remove('is-exiting', 'ac-ref-navigation__stage-image--current');
      previous.classList.add('ac-ref-navigation__stage-image--incoming');
      next.classList.remove('is-entering', 'ac-ref-navigation__stage-image--incoming');
      next.classList.add('ac-ref-navigation__stage-image--current');
      this.#stageCurrent = next;
      this.#stageIncoming = previous;
      this.#activeMediaKey = this.#descriptorKey(descriptor);
    } finally {
      this.#endVisualSwitch();
    }
  }

  #setStableMedia(descriptor) {
    if (!descriptor || !this.#stageCurrent) return;
    this.#applyDescriptor(this.#stageCurrent, descriptor);
    this.#stageCurrent.hidden = false;
    this.#stageCurrent.classList.remove('is-exiting', 'is-entering');
    if (this.#stageIncoming) {
      this.#stageIncoming.hidden = true;
      this.#stageIncoming.classList.remove('is-exiting', 'is-entering');
    }
    this.#activeMediaKey = this.#descriptorKey(descriptor);
  }

  #resetMediaLayers() {
    this.#stageCurrent?.classList.remove('is-exiting', 'is-entering');
    if (this.#stageCurrent) this.#stageCurrent.hidden = false;
    this.#stageIncoming?.classList.remove('is-exiting', 'is-entering');
    if (this.#stageIncoming) this.#stageIncoming.hidden = true;
  }

  #scheduleCollage(url) {
    window.clearTimeout(this.#collageTimer);
    this.#collageAbortController?.abort();
    const request = ++this.#collageContextRequest;
    if (!url) {
      this.#restoreDefaultCollage();
      return;
    }
    this.#collageTimer = window.setTimeout(() => this.#loadCollage(url, request), 140);
  }

  async #loadCollage(url, request) {
    if (!this.#collage || request !== this.#collageContextRequest) return;
    if (this.#collageCache.has(url)) {
      this.#requestCollage(this.#collageCache.get(url));
      return;
    }
    this.#collageAbortController?.abort();
    const controller = new AbortController();
    this.#collageAbortController = controller;
    try {
      const requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.set('section_id', 'ac-navigation-collage');
      const response = await fetch(requestUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const content = template.content.querySelector('[data-ac-navigation-collage-response]');
      if (!content || request !== this.#collageContextRequest) return;
      const markup = content.innerHTML;
      this.#collageCache.set(url, markup);
      if (this.#collageCache.size > 12) this.#collageCache.delete(this.#collageCache.keys().next().value);
      this.#requestCollage(markup);
    } catch (error) {
      if (controller.signal.aborted || request !== this.#collageContextRequest) return;
      if (error.name !== 'AbortError') this.#restoreDefaultCollage();
    }
  }

  #requestCollage(markup) {
    if (!markup || !this.#getMode()) return;
    if (markup === this.#activeCollageMarkup) {
      if (!this.#collageTransitionController) return;
      this.#collageGeneration += 1;
      this.#collageTransitionController.abort();
      this.#collageTransitionController = null;
      this.#resetInterruptedCollage();
      return;
    }
    const generation = ++this.#collageGeneration;
    this.#collageTransitionController?.abort();
    this.#collageTransitionController = new AbortController();
    this.#resetInterruptedCollage();
    void this.#transitionCollage(markup, generation, this.#collageTransitionController);
  }

  async #transitionCollage(markup, generation, controller) {
    if (!this.#collageCurrent || !this.#collageIncoming) return;
    if (acNavigationPrefersReducedMotion(this.#navigation)) {
      this.#setStableCollage(markup);
      if (this.#collageTransitionController === controller) this.#collageTransitionController = null;
      return;
    }
    this.#collageIncoming.innerHTML = markup;
    this.#collageIncoming.hidden = false;
    await this.#waitForCollageImages(this.#collageIncoming, controller.signal);
    if (controller.signal.aborted || generation !== this.#collageGeneration || !this.#getMode()) return;
    this.#collageIncoming.getBoundingClientRect();
    this.#beginVisualSwitch();
    try {
      this.#collageCurrent.classList.add('is-exiting');
      this.#collageIncoming.classList.add('is-entering');
      const products = [...this.#collageIncoming.querySelectorAll('.ac-ref-navigation__product')]
        .filter((product) => product.getClientRects().length > 0);
      await Promise.all(products.map((product) => acWaitForTransition(product, {
        reduced: acNavigationPrefersReducedMotion(this.#navigation),
        signal: controller.signal,
      })));
      if (controller.signal.aborted || generation !== this.#collageGeneration || !this.#getMode()) return;
      const previous = this.#collageCurrent;
      const next = this.#collageIncoming;
      previous.hidden = true;
      previous.innerHTML = '';
      previous.classList.remove('is-exiting', 'ac-ref-navigation__collage-group--current');
      previous.classList.add('ac-ref-navigation__collage-group--incoming');
      next.classList.remove('is-entering', 'ac-ref-navigation__collage-group--incoming');
      next.classList.add('ac-ref-navigation__collage-group--current');
      this.#collageCurrent = next;
      this.#collageIncoming = previous;
      this.#activeCollageMarkup = markup;
    } finally {
      this.#endVisualSwitch();
      if (this.#collageTransitionController === controller) this.#collageTransitionController = null;
    }
  }

  async #waitForCollageImages(group, signal) {
    const images = [...group.querySelectorAll('img')];
    await Promise.all(images.map(async (image) => {
      image.loading = 'eager';
      if (image.complete) {
        if (!signal.aborted && image.naturalWidth > 0 && image.decode) {
          try {
            await image.decode();
          } catch (_error) {
            // A completed image is sufficient when decode is unsupported or interrupted.
          }
        }
        return;
      }
      if (signal.aborted) return;
      await new Promise((resolve) => {
        const finish = () => {
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);
          signal.removeEventListener('abort', finish);
          resolve();
        };
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
        signal.addEventListener('abort', finish, { once: true });
      });
      if (!signal.aborted && image.decode) {
        try {
          await image.decode();
        } catch (_error) {
          // A load event is sufficient when decode is unsupported or interrupted.
        }
      }
    }));
  }

  #restoreDefaultCollage() {
    window.clearTimeout(this.#collageTimer);
    this.#collageContextRequest += 1;
    this.#collageAbortController?.abort();
    this.#requestCollage(this.#defaultCollageMarkup);
  }

  #setStableCollage(markup) {
    if (!this.#collageCurrent) return;
    this.#collageCurrent.innerHTML = markup;
    this.#collageCurrent.hidden = false;
    this.#collageCurrent.classList.remove('is-exiting', 'is-entering');
    if (this.#collageIncoming) {
      this.#collageIncoming.innerHTML = '';
      this.#collageIncoming.hidden = true;
      this.#collageIncoming.classList.remove('is-exiting', 'is-entering');
    }
    this.#activeCollageMarkup = markup;
  }

  #resetInterruptedCollage() {
    this.#collageCurrent?.classList.remove('is-exiting', 'is-entering');
    if (this.#collageCurrent) this.#collageCurrent.hidden = false;
    if (!this.#collageIncoming) return;
    this.#collageIncoming.classList.remove('is-exiting', 'is-entering');
    this.#collageIncoming.hidden = true;
    this.#collageIncoming.innerHTML = '';
  }

  #beginVisualSwitch() {
    this.#visualSwitches += 1;
    this.#onVisualState(true);
  }

  #endVisualSwitch() {
    this.#visualSwitches = Math.max(0, this.#visualSwitches - 1);
    if (this.#visualSwitches === 0) this.#onVisualState(false);
  }

  #descriptorFromTemplate(template) {
    if (!(template instanceof HTMLTemplateElement)) return null;
    return this.#descriptorFromImage(template.content.querySelector('img'));
  }

  #descriptorFromImage(image) {
    if (!(image instanceof HTMLImageElement)) return null;
    return {
      src: image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
      sizes: image.getAttribute('sizes') || '',
      width: image.getAttribute('width') || '',
      height: image.getAttribute('height') || '',
      style: image.getAttribute('style') || '',
    };
  }

  #descriptorKey(descriptor) {
    return descriptor ? `${descriptor.src}|${descriptor.srcset}|${descriptor.sizes}` : '';
  }

  #applyDescriptor(image, descriptor) {
    if (!(image instanceof HTMLImageElement) || !descriptor?.src) return;
    image.decoding = 'async';
    // sizes must precede srcset, including on the detached decoding image.
    for (const attribute of ['sizes', 'srcset', 'width', 'height', 'style']) {
      if (descriptor[attribute]) image.setAttribute(attribute, descriptor[attribute]);
      else image.removeAttribute(attribute);
    }
    image.src = descriptor.src;
  }

  #preloadImage(descriptor, signal) {
    const key = `${this.#descriptorKey(descriptor)}|${window.innerWidth}|${window.devicePixelRatio}`;
    if (this.#imageLoads.has(key)) return this.#imageLoads.get(key);
    const loading = this.#decodeImage(descriptor, signal).then((loaded) => {
      if (!loaded) this.#imageLoads.delete(key);
      return loaded;
    });
    this.#imageLoads.set(key, loading);
    if (this.#imageLoads.size > 12) this.#imageLoads.delete(this.#imageLoads.keys().next().value);
    return loading;
  }

  #decodeImage(descriptor, signal) {
    return new Promise((resolve) => {
      if (signal.aborted) { resolve(false); return; }
      const image = new Image();
      let settled = false;
      const finish = (loaded) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        signal.removeEventListener('abort', abort);
        image.onload = image.onerror = null;
        if (!loaded) {
          image.removeAttribute('srcset');
          image.removeAttribute('src');
        }
        resolve(loaded);
      };
      const abort = () => finish(false);
      const timer = window.setTimeout(abort, 8000);
      signal.addEventListener('abort', abort, { once: true });
      image.onerror = abort;
      image.onload = () => finish(image.naturalWidth > 0);
      this.#applyDescriptor(image, descriptor);
      if (image.decode) {
        image.onload = null;
        image.decode().then(() => finish(image.naturalWidth > 0), abort);
      } else if (image.complete) finish(image.naturalWidth > 0);
    });
  }

}

class AcReferenceHeader extends HTMLElement {
  #abortController;
  #dependencyController;
  #header;
  #lifecycleGeneration = 0;
  #mobileQuery = window.matchMedia('(max-width: 1023px)');
  #mode = null;
  #navigation;
  #navigationFooterObserver;
  #previewController;
  #shellController;
  #treeController;
  #viewController;

  connectedCallback() {
    this.#dependencyController?.abort();
    this.#dependencyController = new AbortController();
    document.addEventListener('ac:overlay-readiness', this.#syncReadiness, { signal: this.#dependencyController.signal });
    this.#syncReadiness();
  }

  #syncReadiness = () => {
    const ready = Boolean(window.AcLayerManager && document.querySelector('ac-overlay-system[data-ready="true"]'));
    if (ready && !this.hasAttribute('data-navigation-ready')) this.#connectNavigation();
    else if (!ready && this.hasAttribute('data-navigation-ready')) this.#disconnectNavigation();
  };

  #connectNavigation() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#header = this.querySelector('[data-ac-ref-header]');
    this.#navigation = this.querySelector('[data-ac-ref-navigation]');
    if (!this.#header || !this.#navigation) return;
    const signal = this.#abortController.signal;
    this.#shellController = new AcHeaderShellController(this, this.#header);
    this.#previewController = new AcNavigationPreviewController({
      navigation: this.#navigation,
      getMode: () => this.#mode,
      onVisualState: (switching) => {
        if (signal.aborted) return;
        if (switching && ['open', 'switching'].includes(this.#navigation.dataset.phase)) this.#setPhase('switching');
        else if (!switching && this.#navigation.dataset.phase === 'switching') this.#setPhase('open');
      },
    });
    this.#treeController = new AcNavigationTreeController({
      navigation: this.#navigation,
      mobileQuery: this.#mobileQuery,
      onPreview: (item) => this.#previewController.previewItem(item),
      onLeave: () => this.#previewController.restoreMode(),
      onResize: this.#syncNavigationFooterReserve,
    });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('open', this.#handleAccountOpen, { capture: true, signal });
    this.#navigation.addEventListener('toggle', this.#handleLocalizationToggle, { capture: true, signal });
    this.#navigation.addEventListener('ac:filterable-list:change', this.#handleFilterableListChange, { signal });
    window.addEventListener('resize', this.#handleResize, { passive: true, signal });
    this.#shellController.connect(signal);
    this.#treeController.connect(signal);
    this.#observeNavigationFooter();
    const basic = this.querySelector('.ac-basic-navigation');
    const wasOpen = basic?.open;
    const restoreFocus = basic?.contains(document.activeElement);
    const menuTrigger = this.querySelector('[data-ac-navigation-trigger="menu"]');
    if (basic) basic.open = false;
    menuTrigger.setAttribute('aria-controls', this.#navigation.id);
    menuTrigger.setAttribute('aria-haspopup', 'dialog');
    menuTrigger.setAttribute('aria-expanded', 'false');
    menuTrigger.setAttribute('aria-label', menuTrigger.dataset.openLabel);
    this.setAttribute('data-navigation-ready', '');
    if (wasOpen) void this.#open('menu', menuTrigger);
    else if (restoreFocus) menuTrigger.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    this.#dependencyController?.abort();
    this.#disconnectNavigation();
  }

  #disconnectNavigation() {
    const restoreFocus = this.isConnected && this.contains(document.activeElement);
    this.#abortController?.abort();
    this.#navigationFooterObserver?.disconnect();
    this.#previewController?.disconnect();
    this.#shellController?.disconnect();
    this.#lifecycleGeneration += 1;
    this.#finishClose(false);
    this.removeAttribute('data-navigation-ready');
    this.classList.remove('is-footer-hidden');
    this.#header?.classList.remove('is-compact');
    this.dataset.shellState = 'top';
    const menuTrigger = this.querySelector('[data-ac-navigation-trigger="menu"]');
    for (const attribute of ['aria-controls', 'aria-haspopup', 'aria-expanded', 'aria-label']) menuTrigger?.removeAttribute(attribute);
    if (restoreFocus) this.querySelector('.ac-basic-navigation__summary')?.focus({ preventScroll: true });
  }

  #handleClick = (event) => {
    if (!event.target.closest('[data-ac-navigation-localization-disclosure]')) this.#closeLocalizationDisclosures();
    const navigationTrigger = event.target.closest('[data-ac-navigation-trigger]');
    if (navigationTrigger && this.contains(navigationTrigger)) {
      event.preventDefault();
      event.stopPropagation();
      this.#toggle(navigationTrigger.dataset.acNavigationTrigger, navigationTrigger);
      return;
    }
    if (this.#treeController.handleAction(event.target)) {
      event.preventDefault();
      return;
    }
    if (this.#mode && event.target.closest('[data-ac-ref-open]')) this.#close(false);
  };

  #handleAccountOpen = (event) => {
    if (!event.target.matches?.('shopify-account')) return;
    event.target.style.setProperty('--shopify-account-dialog-position-top', `${Math.max(0, Math.round(event.target.getBoundingClientRect().bottom))}px`);
    if (this.#mode) this.#close(false);
  };

  #handleLocalizationToggle = (event) => {
    const details = event.target.closest?.('.ac-ref-localization__details');
    if (!details) return;
    if (details.open) this.#closeLocalizationDisclosures(details);
    requestAnimationFrame(this.#treeController.syncScrollAffordances);
  };

  #handleResize = () => {
    if (this.#navigation.hidden) return;
    this.#syncNavigationFooterReserve();
    this.#treeController.syncScrollAffordances();
  };

  #handleFilterableListChange = () => requestAnimationFrame(this.#treeController.syncScrollAffordances);

  #handleLayerEscape = () => {
    if (this.#mode && !this.#treeController.handleEscape()) this.#close(true);
  };

  #toggle(mode, trigger) {
    if (this.#mode === mode) this.#close(true);
    else void this.#open(mode, trigger);
  }

  async #open(mode, trigger) {
    const view = this.querySelector(`[data-ac-navigation-view="${CSS.escape(mode)}"]`);
    if (!view) return;
    const generation = ++this.#lifecycleGeneration;
    const firstOpen = !this.#mode;
    this.#mode = mode;
    window.AcLayerManager?.acquire('navigation', {
      opener: trigger,
      getFocusRoots: () => [this],
      onEscape: this.#handleLayerEscape,
    });
    this.#navigation.hidden = false;
    this.#navigation.inert = false;
    this.#navigation.setAttribute('aria-hidden', 'false');
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('aria-labelledby', this.#navigation.dataset.titleId);
    this.classList.add('is-navigation-open');
    this.#shellController.setNavigationOpen(true);

    const title = this.querySelector('[data-ac-navigation-title]');
    if (title) title.textContent = trigger.dataset.dialogLabel;
    for (const modeTrigger of this.querySelectorAll('[data-ac-navigation-trigger]')) {
      const current = modeTrigger === trigger;
      modeTrigger.setAttribute('aria-expanded', String(current));
      modeTrigger.setAttribute('aria-label', current ? modeTrigger.dataset.closeLabel : modeTrigger.dataset.openLabel);
    }
    this.#treeController.resetHierarchy(true);
    this.#closeLocalizationDisclosures();
    if (firstOpen) {
      this.#previewController.cancel();
      this.#setPhase('preparing');
      await this.#switchView(view, true);
      if (generation !== this.#lifecycleGeneration || this.#mode !== mode) return;
      this.#previewController.setStableMode(mode);
      this.#treeController.syncScrollAffordances();
      this.#navigation.getBoundingClientRect();
      document.dispatchEvent(new CustomEvent('ac:navigation:opened'));
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (generation !== this.#lifecycleGeneration || this.#mode !== mode) return;
      this.#setPhase('opening');
      this.#syncNavigationFooterReserve();
      this.#treeController.syncScrollAffordances();
      this.#focusFirst(view);
      await acWaitForTransition(this.#navigation, {
        property: 'transform',
        pseudoElement: '::before',
        fallback: acNavigationMotionValue(this.#navigation, '--ac-navigation-open-duration'),
        reduced: acNavigationPrefersReducedMotion(this.#navigation),
      });
    } else {
      this.#setPhase('switching');
      await this.#switchView(view, false);
      if (generation !== this.#lifecycleGeneration || this.#mode !== mode) return;
      this.#previewController.requestMedia(this.#previewController.modeMedia(mode));
      this.#syncNavigationFooterReserve();
      this.#treeController.syncScrollAffordances();
      this.#focusFirst(view);
    }
    if (generation !== this.#lifecycleGeneration || this.#mode !== mode) return;
    this.#setPhase('open');
    this.#syncNavigationFooterReserve();
    this.#treeController.syncScrollAffordances();
  }

  /** Close the managed menu before another global surface takes focus. */
  async close({ restoreFocus = true } = {}) {
    await this.#close(restoreFocus);
    await new Promise(resolve => requestAnimationFrame(resolve));
  }

  #close(restoreFocus) {
    if (!this.#mode) return;
    const generation = ++this.#lifecycleGeneration;
    this.#mode = null;
    this.#viewController?.abort();
    this.#treeController.resetHierarchy(false);
    this.#previewController.cancel();
    this.#setPhase('closing');
    this.#navigation.setAttribute('aria-hidden', 'true');
    this.#navigation.inert = true;
    this.removeAttribute('role');
    this.removeAttribute('aria-modal');
    this.removeAttribute('aria-labelledby');
    this.classList.remove('is-navigation-open');
    this.#shellController.setNavigationOpen(false);
    for (const trigger of this.querySelectorAll('[data-ac-navigation-trigger]')) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', trigger.dataset.openLabel);
    }
    document.dispatchEvent(new CustomEvent('ac:navigation:closed'));
    return this.#completeClose(generation, restoreFocus);
  }

  async #completeClose(generation, restoreFocus) {
    await acWaitForTransition(this.#navigation, {
      property: 'transform',
      pseudoElement: '::before',
      fallback: acNavigationMotionValue(this.#navigation, '--ac-navigation-close-duration'),
      reduced: acNavigationPrefersReducedMotion(this.#navigation),
    });
    if (generation !== this.#lifecycleGeneration || this.#mode) return;
    this.#finishClose(restoreFocus);
  }

  #finishClose(restoreFocus) {
    if (!this.#navigation) return;
    this.#viewController?.abort();
    for (const view of this.querySelectorAll('[data-ac-navigation-view]')) view.classList.remove('is-entering', 'is-exiting');
    this.#mode = null;
    this.#navigation.hidden = true;
    this.#navigation.inert = true;
    this.#navigation.setAttribute('aria-hidden', 'true');
    this.#setPhase('closed');
    this.removeAttribute('role');
    this.removeAttribute('aria-modal');
    this.removeAttribute('aria-labelledby');
    this.classList.remove('is-navigation-open');
    this.#shellController?.setNavigationOpen(false);
    for (const trigger of this.querySelectorAll('[data-ac-navigation-trigger]')) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', trigger.dataset.openLabel);
    }
    this.#treeController?.resetHierarchy(true);
    this.#closeLocalizationDisclosures();
    this.#previewController?.setStableMode('menu');
    window.AcLayerManager?.release('navigation', { restoreFocus });
  }

  async #switchView(view, immediate) {
    this.#viewController?.abort();
    const controller = new AbortController();
    this.#viewController = controller;
    for (const modeView of this.querySelectorAll('[data-ac-navigation-view]')) modeView.classList.remove('is-entering', 'is-exiting');
    const current = [...this.querySelectorAll('[data-ac-navigation-view]')].find((candidate) => !candidate.hidden && candidate !== view);
    if (!current || immediate || acNavigationPrefersReducedMotion(this.#navigation)) {
      for (const modeView of this.querySelectorAll('[data-ac-navigation-view]')) modeView.hidden = modeView !== view;
      return;
    }
    current.classList.add('is-exiting');
    view.hidden = false;
    view.classList.add('is-entering');
    view.getBoundingClientRect();
    requestAnimationFrame(() => { if (!controller.signal.aborted) view.classList.remove('is-entering'); });
    await acWaitForTransition(current.querySelector('.ac-ref-navigation__item-motion'), {
      fallback: acNavigationMotionValue(this.#navigation, '--ac-navigation-text-duration'),
      reduced: acNavigationPrefersReducedMotion(this.#navigation),
      signal: controller.signal,
    });
    if (controller.signal.aborted) return;
    current.hidden = true;
    current.classList.remove('is-exiting');
  }

  #focusFirst(view) {
    if (document.documentElement.dataset.acInputMode !== 'keyboard') return;
    const generation = this.#lifecycleGeneration;
    const signal = this.#abortController?.signal;
    requestAnimationFrame(() => {
      if (signal?.aborted || generation !== this.#lifecycleGeneration || !this.#mode || view.hidden
        || document.documentElement.dataset.acInputMode !== 'keyboard') return;
      const firstAction = acNavigationFocusableElements(view).find((element) => !element.matches('[data-ac-navigation-scroll]'));
      firstAction?.focus({ preventScroll: true });
    });
  }

  #closeLocalizationDisclosures(except = null) {
    for (const details of this.#navigation?.querySelectorAll('.ac-ref-localization__details[open]') || []) {
      if (details === except) continue;
      details.open = false;
    }
  }

  #observeNavigationFooter() {
    const footer = this.#navigation.querySelector('.ac-ref-navigation__footer');
    if (!footer || !('ResizeObserver' in window)) return;
    this.#navigationFooterObserver = new ResizeObserver(this.#syncNavigationFooterReserve);
    this.#navigationFooterObserver.observe(footer);
    this.#syncNavigationFooterReserve();
  }

  #syncNavigationFooterReserve = () => {
    const footer = this.#navigation?.querySelector('.ac-ref-navigation__footer');
    if (!footer || this.#navigation.hidden) return;
    const reserve = Math.max(0, window.innerHeight - footer.getBoundingClientRect().top);
    this.#navigation.style.setProperty('--ac-navigation-footer-reserve', `${Math.ceil(reserve)}px`);
  };

  #setPhase(phase) {
    this.#navigation.dataset.phase = phase;
  }

}

if (!customElements.get('ac-reference-header')) customElements.define('ac-reference-header', AcReferenceHeader);
